import { memo } from 'react'
import { getStrategyMeta } from '../lib/strategy'
import type { SimulationResult } from '../types'
import { InfoTip } from './InfoTip'

const pct = (v: number) => `${(v * 100).toFixed(1)}%`
const maybePct = (v: number | null) => (v === null ? '—' : pct(v))
const maybeNum = (v: number | null) => (v === null ? '—' : v.toFixed(2))

export const KpiGrid = memo(function KpiGrid({ result }: { result: SimulationResult }) {
  const s = result.summary
  const meta = getStrategyMeta(result.inputs.strategy)
  const volEdge = result.inputs.impliedVol - result.inputs.realizedVol
  const cards = [
    [`${meta.shortLabel} 平均收益`, pct(s.meanStrategyReturn), '所有 Monte Carlo 路径的终值总收益平均值。'],
    [`${meta.benchmarkLabel} 平均收益`, pct(s.meanBenchmarkReturn), '对应基准的 Monte Carlo 平均总收益。'],
    ['IV − RV', (volEdge >= 0 ? '+' : '') + (volEdge * 100).toFixed(1) + ' vol pts', '卖波动率策略最重要的输入差异之一；高 IV 本身不等于高收益，关键是相对未来实现波动率的溢价。'],
    ['跑赢基准概率', pct(s.probabilityOutperform), '策略终值高于对应基准的路径比例。'],
    ['Gross Short Premium', pct(s.averagePremiumIncomePct), '所有 Short Option 腿累计收到的毛权利金 / 初始资本，不是利润。'],
    ['Long Option Debit', pct(s.averageOptionDebitPct), '所有 Long Option 腿累计支付的毛权利金 / 初始资本。'],
    ['Net Option P&L', pct(s.averageNetOptionPnlPct), '所有 Long/Short Call/Put 腿的入场现金流、结算、期末 MTM 与交易成本合计。'],
    ['ITM Settlement Rate', pct(s.averageSettlementRate), '到期产生正 payoff 的期权腿比例；多腿策略按 leg expiry 统计，不等于交易胜率。'],
    ['权益≤0 路径概率', pct(s.probabilityEquityBelowZero), '模拟期间经济权益曾达到 0 或以下的路径比例。'],
    ['Margin breach 概率', pct(s.probabilityMarginBreach), '仅在 Reg-T Approx 开启时有效；表示研究级保证金近似触发强制平仓的路径比例。'],
    ['亏损≥50% 概率', pct(s.probabilityLoss50), '终值总收益低于或等于 -50% 的路径比例。'],
    ['终值≤25%初始资本', pct(s.probabilityEquityBelow25Pct), '终值权益不高于初始资本 25% 的路径比例。'],
    ['P01 终值收益', pct(s.p01), '终值收益分布的 1% 分位数，比单纯 P05 更能展示短波动率结构的左尾。'],
    ['5% Expected Shortfall', pct(s.expectedShortfall05), '最差 5% 路径终值收益的平均值。'],
    ['最差模拟路径', pct(s.worstReturn), '本次有限 Monte Carlo 样本中的最差终值；不是理论最大亏损。'],
    ['平均最大回撤', pct(s.meanMaxDrawdown), '逐日 MTM 财富曲线最大回撤的路径平均；负权益时可超过 100%。'],
    ['Median CAGR', maybePct(s.medianStrategyCagr), '对终值中位数做几何年化；若中位数终值非正则不定义。'],
    ['Median Path Sharpe', maybeNum(s.medianPathSharpe), '逐条路径用日度权益收益计算传统年化 Sharpe，再取中位数；仅对全程权益>0的路径定义。'],
    ['Sharpe Coverage', pct(s.pathSharpeCoverage), '可计算 Path Sharpe 的路径占比。低覆盖率意味着幸存者筛选较强，应优先关注 ES、负权益概率和回撤。'],
    ['Terminal Outcome Ratio', s.terminalOutcomeRatio.toFixed(2), '跨 Monte Carlo 终值分布的风险调整比率；不是时间序列 Sharpe。'],
  ]

  return (
    <div className="kpi-grid">
      {cards.map(([label, value, help]) => (
        <article className="kpi-card" key={label}>
          <div className="kpi-card__label">
            {label}
            <InfoTip>{help}</InfoTip>
          </div>
          <strong>{value}</strong>
        </article>
      ))}
    </div>
  )
})
